import {
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography,
} from "@mui/material";

import { RowChoiceProps } from "./RowBase";
import { ControlPointType } from "../../../../models/controlpoint/ControlPoint";
import { WorkOrderControlPoint } from "../../../../models/workorder/WorkOrder";

interface ControlPointsSplit {
    mechanical: WorkOrderControlPoint[];
    chemical: WorkOrderControlPoint[];
    other: WorkOrderControlPoint[];
}

export function RowControlPoints({ answer, controlPoints }: RowChoiceProps) {
    const splitPoints = controlPoints.reduce<ControlPointsSplit>(
        (prev, curr) => {
            if (
                curr.controlPointType === ControlPointType.MONITOR_MECHANICAL ||
                curr.controlPointType === ControlPointType.FIGHT_MECHANICAL
            ) {
                prev.mechanical.push(curr);
            } else if (
                curr.controlPointType === ControlPointType.MONITOR_NONTOX ||
                curr.controlPointType === ControlPointType.FIGHT_CHEMICAL
            ) {
                prev.chemical.push(curr);
            } else {
                prev.other.push(curr);
            }
            return prev;
        },
        {
            mechanical: [],
            chemical: [],
            other: [],
        },
    );

    return (
        <Stack gap={2}>
            <Typography>Controlepunten</Typography>
            <Typography>
                Type: Monitoren - Mechanisch / Bestrijden - Mechanisch
            </Typography>
            <MechanicalTable
                id={answer.id}
                controlPoints={splitPoints.mechanical}
            />
            <Typography>
                Type: Monitoren - Nontox / Bestrijden - Chemisch
            </Typography>
            <ChemicalTable
                id={answer.id}
                controlPoints={splitPoints.chemical}
            />
            <Typography>Type: Anders</Typography>
            <OtherTable id={answer.id} controlPoints={splitPoints.other} />
        </Stack>
    );
}

interface TableProps {
    id: number;
    controlPoints: WorkOrderControlPoint[];
}

function MechanicalTable({ id, controlPoints }: TableProps) {
    return (
        <Paper>
            <Table>
                <TableHead>
                    <TableRow>
                        <TableCell width="30%">Naam</TableCell>
                        <TableCell width="70%">Aantal</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {controlPoints
                        .map(controlPoint => {
                            if (controlPoint.usages.length === 0) {
                                return null;
                            }

                            const last = controlPoint.usages.at(-1)!;

                            if (last.entries.length === 0) {
                                return null;
                            }

                            const phrases: string[] = [];

                            for (const entry of last.entries) {
                                const added = entry.added ?? 0;
                                if (added === 0) {
                                    phrases.push(added.toString());
                                } else {
                                    phrases.push(`${added} ${entry.pest}`);
                                }
                            }

                            return (
                                <TableRow
                                    key={`workorder_answer_${id}_cp_mechanical_${controlPoint.name}`}
                                >
                                    <TableCell>{controlPoint.name}</TableCell>
                                    <TableCell>
                                        <pre
                                            style={{
                                                whiteSpace: "pre-line",
                                                wordWrap: "break-word",
                                                margin: 0,
                                            }}
                                        >
                                            {phrases.join("\n")}
                                        </pre>
                                    </TableCell>
                                </TableRow>
                            );
                        })
                        .filter(f => f !== null)}
                    {controlPoints.length === 0 && (
                        <TableRow>
                            <TableCell colSpan={4}>
                                <Typography>Geen controlepunten</Typography>
                            </TableCell>
                        </TableRow>
                    )}
                </TableBody>
            </Table>
        </Paper>
    );
}

function ChemicalTable({ id, controlPoints }: TableProps) {
    return (
        <Paper>
            <Table>
                <TableHead>
                    <TableRow>
                        <TableCell width="30%">Naam</TableCell>
                        <TableCell width="15%">Begin</TableCell>
                        <TableCell width="20%">Toegevoegd</TableCell>
                        <TableCell width="20%">Opgenomen</TableCell>
                        <TableCell width="15%">Eind</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {controlPoints
                        .map(controlPoint => {
                            if (controlPoint.usages.length === 0) {
                                return null;
                            }

                            const others = controlPoint.usages.slice(0, -1);
                            const last = controlPoint.usages.at(-1)!;

                            if (last.entries.length === 0) {
                                return null;
                            }

                            const startValue = others
                                .flatMap(usage => usage.entries)
                                .reduce(
                                    (prev, curr) =>
                                        (prev +=
                                            (curr.added ?? 0) -
                                            (curr.removed ?? 0)),
                                    0,
                                );
                            const addedValue = last.entries.at(0)?.added ?? 0;
                            const removedValue =
                                last.entries.at(0)?.removed ?? 0;
                            const endValue =
                                startValue + addedValue - removedValue;

                            return (
                                <TableRow
                                    key={`workorder_answer_${id}_cp_chemical_${controlPoint.name}`}
                                >
                                    <TableCell>{controlPoint.name}</TableCell>
                                    <TableCell>{startValue}</TableCell>
                                    <TableCell>{addedValue}</TableCell>
                                    <TableCell>{removedValue}</TableCell>
                                    <TableCell>{endValue}</TableCell>
                                </TableRow>
                            );
                        })
                        .filter(f => f !== null)}
                    {controlPoints.length === 0 && (
                        <TableRow>
                            <TableCell colSpan={5}>
                                <Typography>Geen controlepunten</Typography>
                            </TableCell>
                        </TableRow>
                    )}
                </TableBody>
            </Table>
        </Paper>
    );
}

function OtherTable({ id, controlPoints }: TableProps) {
    return (
        <Paper>
            <Table>
                <TableHead>
                    <TableRow>
                        <TableCell width="30%">Naam</TableCell>
                        <TableCell width="75%">Omschrijving</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {controlPoints
                        .map(controlPoint => {
                            if (controlPoint.usages.length === 0) {
                                return null;
                            }

                            const last = controlPoint.usages.at(-1)!;

                            if (last.entries.length === 0) {
                                return null;
                            }

                            return (
                                <TableRow
                                    key={`workorder_answer_${id}_cp_other_${controlPoint.name}`}
                                >
                                    <TableCell>{controlPoint.name}</TableCell>
                                    <TableCell>
                                        {last.entries.at(0)?.rawInput}
                                    </TableCell>
                                </TableRow>
                            );
                        })
                        .filter(f => f !== null)}
                    {controlPoints.length === 0 && (
                        <TableRow>
                            <TableCell colSpan={2}>
                                <Typography>Geen controlepunten</Typography>
                            </TableCell>
                        </TableRow>
                    )}
                </TableBody>
            </Table>
        </Paper>
    );
}
